import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { ErrorLoggerService } from './error-logger.service';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class DevInstrumentationService {
  private readonly errorLogger = inject(ErrorLoggerService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private installed = false;

  install(): void {
    // Local dev only — dev.app/prod have no `/__log` server to receive the logs.
    if (this.installed || !environment.devLogging) return;
    this.installed = true;

    this.patchFetch();
    this.installRouterLogging();
    this.installGlobalErrorHandlers();
    this.installClickLogging();
    this.installVisibilityLogging();

    this.errorLogger.info(
      `instrumentation installed (${navigator.userAgent.split(' ').pop()}) viewport=${window.innerWidth}x${window.innerHeight}`,
      { context: 'DevInstrumentation.install' },
    );
  }

  private patchFetch(): void {
    const original = window.fetch.bind(window);
    const logger = this.errorLogger;
    const shorten = (url: string): string => this.shortenUrl(url);

    window.fetch = async function patchedFetch(
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
          ? input.toString()
          : input.url;
      const method = (
        init?.method ?? (input instanceof Request ? input.method : 'GET')
      ).toUpperCase();

      // Skip the dev log endpoint itself to avoid feedback loops
      if (url.includes('/__log')) return original(input, init);

      const start = performance.now();
      const shortUrl = shorten(url);
      logger.debug(`HTTP → ${method} ${shortUrl}`, { context: 'http' });

      try {
        const response = await original(input, init);
        const duration = Math.round(performance.now() - start);
        const level = response.ok ? 'debug' : 'warn';
        const msg = `HTTP ← ${response.status} ${method} ${shortUrl} (${duration}ms)`;
        if (level === 'warn') logger.warn(msg, { context: 'http' });
        else logger.debug(msg, { context: 'http' });
        return response;
      } catch (error) {
        const duration = Math.round(performance.now() - start);
        const errMsg = error instanceof Error ? error.message : String(error);
        logger.warn(`HTTP ✗ ${method} ${shortUrl} (${duration}ms): ${errMsg}`, {
          context: 'http',
        });
        throw error;
      }
    };
  }

  private shortenUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      const sameOrigin = parsed.origin === window.location.origin;
      const path = parsed.pathname + parsed.search;
      const display = sameOrigin ? path : parsed.host + path;
      return display.length > 160 ? display.slice(0, 160) + '…' : display;
    } catch {
      return url.length > 160 ? url.slice(0, 160) + '…' : url;
    }
  }

  private installRouterLogging(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.errorLogger.info(
          `route → ${event.url} (trigger=${event.navigationTrigger})`,
          { context: 'router' },
        );
      } else if (event instanceof NavigationEnd) {
        this.errorLogger.info(`route ✓ ${event.urlAfterRedirects}`, {
          context: 'router',
        });
      } else if (event instanceof NavigationCancel) {
        this.errorLogger.warn(
          `route ✗ cancelled: ${event.url} (${event.reason || 'unknown'})`,
          { context: 'router' },
        );
      } else if (event instanceof NavigationError) {
        const errMsg =
          (event.error as Error)?.message ?? String(event.error);
        this.errorLogger.warn(`route ✗ error: ${event.url} — ${errMsg}`, {
          context: 'router',
        });
      }
    });
  }

  private installGlobalErrorHandlers(): void {
    window.addEventListener('error', (event) => {
      this.errorLogger.log(event.error ?? event.message, undefined, {
        context: `window.error (${event.filename}:${event.lineno}:${event.colno})`,
      });
    });
    window.addEventListener('unhandledrejection', (event) => {
      this.errorLogger.log(event.reason, undefined, {
        context: 'unhandledrejection',
      });
    });
  }

  private installClickLogging(): void {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target as Element | null;
        if (!target) return;

        const interactive = target.closest(
          'button, a, [role="button"], mat-checkbox, mat-slide-toggle, mat-option, [mat-menu-item], .mat-mdc-tab',
        );
        if (!interactive) return;

        const tag = interactive.tagName.toLowerCase();
        const text = (interactive.textContent ?? '')
          .trim()
          .replace(/\s+/g, ' ')
          .slice(0, 80);
        const id = interactive.id ? `#${interactive.id}` : '';
        const classes = interactive.classList.length
          ? '.' + Array.from(interactive.classList).slice(0, 3).join('.')
          : '';
        const href =
          interactive instanceof HTMLAnchorElement && interactive.href
            ? ` href=${interactive.getAttribute('href')}`
            : '';

        this.errorLogger.debug(
          `click ${tag}${id}${classes}${href} "${text}"`,
          { context: 'ui' },
        );
      },
      true,
    );
  }

  private installVisibilityLogging(): void {
    document.addEventListener('visibilitychange', () => {
      this.errorLogger.debug(`visibility=${document.visibilityState}`, {
        context: 'ui',
      });
    });
  }
}
